import { formatDate } from '@angular/common';

/**
 * Single source of truth for how LedgerSync displays dates and times.
 *
 * Every date/time shown in the UI is rendered in this time zone. To move the
 * whole app to a different zone later, change APP_TIME_ZONE and nothing else.
 *
 * Rendering flows through {@link LsDatePipe} (which transparently replaces
 * Angular's DatePipe app-wide), so individual components never deal with time
 * zones directly.
 */

/**
 * IANA time zone the UI renders in. 'America/New_York' is daylight-saving
 * aware, so timestamps automatically show as EST (UTC-5) in winter and
 * EDT (UTC-4) in summer — always matching a wall clock in New York.
 */
export const APP_TIME_ZONE = 'America/New_York';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_NO_TZ = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
const HAS_TZ = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * True when the value is a bare calendar date with no time component
 * (e.g. "2026-07-01"). Such values carry no instant, so they must be rendered
 * as-is rather than converted to the app time zone — see {@link formatCalendarDate}.
 */
export function isDateOnlyString(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

/**
 * Normalizes a raw value coming from the backend into a real instant.
 *
 * The backend serializes timestamps as GMT wall-clock time WITHOUT a time-zone
 * designator (e.g. "2026-07-10T15:00:00"). Left alone, the browser would parse
 * that as local time and be wrong. We treat any tz-less date/time as UTC.
 *
 * Returns null for empty/invalid input so callers render a blank instead of
 * "Invalid Date".
 */
export function toAppInstant(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  let normalized = value.trim();
  if (!HAS_TZ.test(normalized)) {
    if (DATETIME_NO_TZ.test(normalized)) {
      // GMT wall-clock without a tz -> pin it to UTC.
      normalized = normalized.replace(' ', 'T') + 'Z';
    } else if (DATE_ONLY.test(normalized)) {
      // Date-only value -> treat as a UTC-midnight instant.
      normalized = normalized + 'T00:00:00Z';
    }
  }
  const parsed = new Date(normalized);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Returns the numeric offset of {@link APP_TIME_ZONE} for the given instant,
 * formatted the way Angular's formatDate() expects (e.g. "-0400" / "-0500").
 *
 * The offset is resolved per-instant via Intl, so it is correct across daylight
 * saving boundaries — a summer date yields -0400, a winter date -0500.
 */
export function appTimeZoneOffset(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    timeZoneName: 'longOffset',
  }).formatToParts(date);

  const tzName = parts.find(part => part.type === 'timeZoneName')?.value ?? 'GMT+00:00';
  const match = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(tzName);
  if (!match) {
    return '+0000';
  }
  const [, sign, hours, minutes] = match;
  return `${sign}${hours.padStart(2, '0')}${(minutes ?? '00').padStart(2, '0')}`;
}

/**
 * Formats a *calendar date* — a day with no meaningful time-of-day, such as a
 * close-period or statement boundary — exactly as given, with NO time-zone
 * conversion.
 *
 * Use this (never the app time zone) for user-picked dates and date-only
 * values: a close period of "July 1 - July 31" must read the same in Madrid,
 * New York, or anywhere else. Applying an offset would show the wrong day
 * (e.g. shifting July 1 back to June 30).
 */
export function formatCalendarDate(
  value: string | Date | null | undefined,
  format = 'longDate',
  locale = 'en-US',
): string {
  const date = toCalendarDate(value);
  return date ? formatDate(date, format, locale) : '';
}

/**
 * Parses a *calendar date* into a local-midnight Date, with NO time-zone
 * conversion — the parsing counterpart of {@link formatCalendarDate}.
 *
 * Use this instead of `new Date(value)` for date-only values: the native parser
 * treats a bare "2026-07-16" as UTC midnight, which lands on the previous day
 * for any viewer west of GMT (so a date picker would open on the wrong day).
 */
export function toCalendarDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (dateOnly) {
    // Build a local-midnight date from the calendar parts so the rendered day
    // never shifts with the viewer's time zone.
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}
